import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { IconPlus } from "@/components/Icons";
import {
  Button,
  Empty,
  Filters,
  Kbd,
  PageHead,
  SearchField,
  Spacer,
  Stack,
  Problem,
  Table,
  Td,
  Th,
  Toolbar,
  type AppliedFilter,
  type FilterField,
} from "@/components/kit";
import { fetchRecords } from "@/lib/api/browser";
import type { FieldDef, MasterDefinition, MasterRecord } from "@/lib/api/types";

/**
 * A master's records — the screen a season's loading actually happens on.
 *
 * Everything about it comes from the master's own declaration: the columns, the
 * filters, and what each filter offers. Nothing here knows a sport.
 *
 * It asks the server rather than filtering in the browser, even though the data
 * would fit today. Searching what happens to be loaded is a different feature
 * that looks identical until the day it silently stops finding things, and the
 * lists here are meant to reach millions.
 *
 * The fetch goes through this app's own route handler, not the admin API — see
 * ``lib/api/browser.ts``. Calling the API from here looked fine and returned
 * nothing on every master page: the base URL is server-only and the API sets no
 * CORS headers, so the request never completed and the screen showed its empty
 * state. A failure now says so instead.
 */

const PAGE = 50;

/** Which of a declared field's types the filter bar can test, and how. */
function filterKind(type: FieldDef["type"]): FilterField["kind"] {
  if (type === "integer" || type === "number" || type === "ratio") return "number";
  if (type === "date" || type === "datetime") return "date";
  if (type === "enum" || type === "boolean" || type === "country") return "choice";
  return "text";
}

function filterFields(master: MasterDefinition): FilterField[] {
  const fields: FilterField[] = [
    { key: "country", label: "Country", kind: "text" },
    ...(master.fields ?? []).map((field) => ({
      key: field.key,
      label: field.label ?? field.key,
      kind: filterKind(field.type),
      options:
        field.type === "boolean"
          ? ["true", "false"]
          : (field.options ?? []).map((o) => String(o.value)),
    })),
  ];
  // A participant master has a country column; a vocabulary does not. Offering
  // a filter that can only ever match nothing is worse than offering none.
  return master.binding.kind === "participant" ? fields : fields.slice(1);
}

function columnsFor(master: MasterDefinition) {
  const fields = master.fields ?? [];
  const keys = master.list?.columns?.length
    ? master.list.columns
    : fields.slice(0, 4).map((f) => f.key);
  return keys.map((key) => ({
    key,
    label: fields.find((f) => f.key === key)?.label ?? key,
    numeric: ["integer", "number", "ratio"].includes(
      fields.find((f) => f.key === key)?.type ?? "text",
    ),
  }));
}

function cell(record: MasterRecord, key: string): string {
  const intrinsic = (record as unknown as Record<string, unknown>)[key];
  const value = intrinsic ?? record.values[key];
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function RecordList({
  sport,
  master,
  writable,
}: {
  sport: string;
  master: MasterDefinition;
  writable: boolean;
}) {
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<AppliedFilter[]>([]);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<MasterRecord[]>([]);
  const [total, setTotal] = useState(0);
  // A master with no store behind it never fetches, so it is never loading —
  // said at the start rather than corrected in an effect a moment later.
  const [loading, setLoading] = useState(writable);
  // A failed fetch and an empty master are the same screen unless one of them
  // says otherwise. That is how this stayed broken.
  const [problem, setProblem] = useState<string | null>(null);

  const fields = useMemo(() => filterFields(master), [master]);
  const columns = useMemo(() => columnsFor(master), [master]);
  const narrowed = query.trim() !== "" || filters.length > 0;

  // Debounced, and the last answer wins: typing "rohit" fires several requests
  // and they do not necessarily come back in order.
  const latest = useRef(0);
  useEffect(() => {
    if (!writable) return;
    const mine = ++latest.current;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const answer = await fetchRecords(sport, master.code, {
          q: query.trim() || undefined,
          filter: filters
            .filter((f) => f.value !== "" || f.test === "is empty")
            .map((f) => `${f.key}:${f.test}:${f.value}`),
          limit: PAGE,
          offset: page * PAGE,
        });
        if (mine !== latest.current) return;
        setRows(answer.rows);
        setTotal(answer.total);
        setProblem(null);
      } catch (error) {
        if (mine !== latest.current) return;
        setRows([]);
        setTotal(0);
        setProblem(error instanceof Error ? error.message : "could not load these records");
      } finally {
        if (mine === latest.current) setLoading(false);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [sport, master.code, query, filters, page, writable]);

  // Narrowing while on page four would otherwise leave you on an empty page
  // four, which reads as "nothing matched". Done here rather than in an effect
  // reacting to the change: the reset belongs to the act of narrowing.
  const narrow = (change: () => void) => {
    change();
    setPage(0);
  };

  const from = page * PAGE;

  return (
    <>
      <PageHead
        title={master.label}
        note={
          loading
            ? "Loading…"
            : narrowed
              ? `${total} matching`
              : `${total} record${total === 1 ? "" : "s"}`
        }
        actions={
          writable && (
            <div style={{ display: "flex", gap: "var(--s2)" }}>
              <Button>
                <Link
                  to={`/${sport}/masters/${master.code}/import`}
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  Import
                </Link>
              </Button>
              <Button tone="primary">
                <Link
                  to={`/${sport}/masters/${master.code}/new`}
                  style={{
                    color: "inherit",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "var(--s2)",
                  }}
                >
                  <IconPlus size={15} />
                  Add {master.label.toLowerCase()}
                </Link>
              </Button>
            </div>
          )
        }
      />

      {!writable ? (
        <Empty
          title={`${master.label} is configured but not yet writable`}
          note="Its binding has no store behind it. The definition is valid; records cannot be entered here."
        />
      ) : (
        <Stack>
          <Toolbar>
            <SearchField
              placeholder={`Search ${master.label.toLowerCase()}`}
              value={query}
              onChange={(e) => narrow(() => setQuery(e.target.value))}
            />
            <Filters
              fields={fields}
              value={filters}
              onChange={(next) => narrow(() => setFilters(next))}
            />
            <Spacer />
            {total > PAGE && (
              <span style={{ fontSize: "var(--t-xs)", color: "var(--ink-3)" }}>
                {from + 1}–{Math.min(from + PAGE, total)} of {total}
              </span>
            )}
          </Toolbar>

          {problem !== null ? (
            <Problem>{problem}</Problem>
          ) : rows.length === 0 && loading ? null : rows.length === 0 ? (
            <Empty
              title={narrowed ? "Nothing matches" : `No ${master.label.toLowerCase()} yet`}
              note={
                narrowed
                  ? "Remove a filter, or widen one."
                  : `Add one, or import a spreadsheet — the blank sheet has ${master.label}'s own columns already.`
              }
              action={
                narrowed ? (
                  <Button
                    onClick={() =>
                      narrow(() => {
                        setQuery("");
                        setFilters([]);
                      })
                    }
                  >
                    Clear
                  </Button>
                ) : (
                  <Button tone="primary">
                    <Link
                      to={`/${sport}/masters/${master.code}/new`}
                      style={{ color: "inherit", textDecoration: "none" }}
                    >
                      Add the first one
                    </Link>
                  </Button>
                )
              }
            />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  {columns.map((c) => (
                    <Th key={c.key} num={c.numeric}>
                      {c.label}
                    </Th>
                  ))}
                  <Th>Code</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((record) => (
                  <tr key={record.code}>
                    <Td strong>
                      <Link
                        to={`/${sport}/masters/${master.code}/${encodeURIComponent(record.code)}/edit`}
                        style={{ color: "inherit", textDecoration: "none" }}
                      >
                        {record.name}
                      </Link>
                    </Td>
                    {columns.map((c) => (
                      <Td key={c.key} num={c.numeric} quiet>
                        {cell(record, c.key)}
                      </Td>
                    ))}
                    <Td quiet>{record.code}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {total > PAGE && (
            <Toolbar>
              <Button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <Button
                disabled={from + PAGE >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
              <Spacer />
              <span style={{ fontSize: "var(--t-xs)", color: "var(--ink-3)" }}>
                Paste a column with <Kbd>⌘</Kbd>
                <Kbd>V</Kbd> on the import screen
              </span>
            </Toolbar>
          )}
        </Stack>
      )}
    </>
  );
}
